import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { request, AddressPurpose, RpcErrorCode } from 'sats-connect';
import { uintCV, principalCV } from '@stacks/transactions';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WalletContext = createContext(null);

// Storage key
const WALLET_STORAGE_KEY = 'stxoshi-wallet';

// === DEVICE DETECTION ===
const isMobile = () => {
  try {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  } catch (e) {
    return false;
  }
};

const isIOS = () => {
  try {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  } catch (e) {
    return false;
  }
};

// === PROVIDER DETECTION ===
// Prioritize XverseProviders to avoid conflicts with Leather
const getXverseProvider = () => {
  try {
    if (typeof window === 'undefined') return null;
    // Specifically get Xverse's provider to avoid Leather conflict
    if (window.XverseProviders?.StacksProvider) {
      console.log('[PROVIDER] Using XverseProviders.StacksProvider');
      return window.XverseProviders.StacksProvider;
    }
    // Fallback to generic (may be Leather if both installed)
    if (window.StacksProvider) {
      console.log('[PROVIDER] Using generic StacksProvider (may be Leather)');
      return window.StacksProvider;
    }
    if (window.BitcoinProvider) {
      console.log('[PROVIDER] Using BitcoinProvider');
      return window.BitcoinProvider;
    }
    return null;
  } catch (e) {
    return null;
  }
};

const getLeatherProvider = () => {
  try {
    if (typeof window === 'undefined') return null;
    if (window.LeatherProvider) return window.LeatherProvider;
    // Leather also injects as HiroWalletProvider on some versions
    if (window.HiroWalletProvider) return window.HiroWalletProvider;
    return null;
  } catch (e) {
    return null;
  }
};

const hasWalletProvider = () => {
  return !!getXverseProvider() || !!getLeatherProvider();
};

// Safe localStorage operations
const safeGetItem = (key) => {
  try { return localStorage.getItem(key); } catch (e) { return null; }
};

const safeSetItem = (key, value) => {
  try { localStorage.setItem(key, value); return true; } catch (e) { return false; }
};

const safeRemoveItem = (key) => {
  try { localStorage.removeItem(key); } catch (e) { /* ignore */ }
};

// Clear wallet session
const clearWalletSession = () => {
  console.log('[WALLET] Clearing session...');
  try {
    safeRemoveItem(WALLET_STORAGE_KEY);
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('wallet') || key.includes('stacks') || key.includes('xverse') || key.includes('sats-connect') || key.includes('leather'))) {
        keys.push(key);
      }
    }
    keys.forEach(k => safeRemoveItem(k));
    try { sessionStorage.clear(); } catch (e) { /* ignore */ }
  } catch (e) {
    console.error('[WALLET] Clear error:', e);
  }
};

// Deep link helper
const openXverseDeepLink = () => {
  try {
    const url = encodeURIComponent(window.location.href);
    if (isIOS()) {
      window.location.href = `https://connect.xverse.app/browser?url=${url}`;
    } else {
      window.location.href = `intent://browser?url=${url}#Intent;scheme=xverse;package=com.xverse.wallet;end`;
    }
  } catch (e) {
    console.error('[WALLET] Deep link error:', e);
  }
};

export function WalletProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [addresses, setAddresses] = useState({ stx: null, btc: null, ordinals: null, publicKey: null });
  const [showWalletModal, setShowWalletModal] = useState(false);
  const txInProgress = useRef(false);

  // Log environment on mount
  useEffect(() => {
    console.log('[WALLET] === INIT ===');
    console.log('[WALLET] UserAgent:', navigator.userAgent);
    console.log('[WALLET] isMobile:', isMobile());
    console.log('[WALLET] hasWalletProvider:', hasWalletProvider());
    console.log('[WALLET] XverseProviders:', !!window?.XverseProviders);
    console.log('[WALLET] StacksProvider:', !!window?.StacksProvider);
    console.log('[WALLET] LeatherProvider:', !!window?.LeatherProvider);
    console.log('[WALLET] BitcoinProvider:', !!window?.BitcoinProvider);
    checkConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkConnection = async () => {
    console.log('[WALLET] checkConnection starting...');
    try {
      // Small delay to ensure localStorage is accessible in mobile browsers
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const stored = safeGetItem(WALLET_STORAGE_KEY);
      console.log('[WALLET] Stored data found:', !!stored);
      
      if (stored) {
        const data = JSON.parse(stored);
        console.log('[WALLET] Parsed STX address:', data?.stx?.slice(0, 10) + '...');
        if (data?.stx) {
          setAddresses(data);
          setConnected(true);
          await fetchUser(data.stx);
          console.log('[WALLET] Connection restored successfully');
        }
      } else {
        console.log('[WALLET] No stored wallet data');
      }
    } catch (e) {
      console.error('[WALLET] Check error:', e);
    } finally {
      setIsLoading(false);
      console.log('[WALLET] checkConnection complete, isLoading=false');
    }
  };

  const fetchUser = async (stxAddr) => {
    try {
      const res = await axios.post(`${API}/auth/connect`, { stx_address: stxAddr });
      setUser(res.data);
      return res.data;
    } catch (e) {
      console.error('[WALLET] Fetch user error:', e);
      return null;
    }
  };

  // Mobile deep link opener
  const openXverseMobile = useCallback(() => {
    toast.info('Opening Xverse...', { duration: 2000 });
    openXverseDeepLink();
    setTimeout(() => {
      toast.info('Not opening? Open Xverse app → Explore → Enter site URL', { duration: 10000 });
    }, 3000);
  }, []);

  const openLeatherMobile = useCallback(() => {
    toast.error('Leather is desktop only - use Xverse on mobile', { duration: 5000 });
  }, []);

  // === CONNECT WALLET ===
  const connectWallet = useCallback(async () => {
    console.log('[WALLET] === CONNECT WALLET ===');
    console.log('[WALLET] Checking for providers...');
    
    setIsLoading(true);
    
    try {
      const mobile = isMobile();
      const hasProvider = hasWalletProvider();
      
      console.log('[WALLET] mobile:', mobile, 'hasProvider:', hasProvider);
      
      // Mobile without provider - show modal to open in Xverse browser
      if (mobile && !hasProvider) {
        console.log('[WALLET] Mobile without provider - showing modal');
        setShowWalletModal(true);
        setIsLoading(false);
        return;
      }
      
      // Desktop without provider - show install message
      if (!mobile && !hasProvider) {
        console.log('[WALLET] Desktop without provider - showing install message');
        toast.error('No wallet extension found', {
          description: (
            <div className="text-xs mt-1">
              Please install a Stacks wallet:
              <ul className="list-disc list-inside mt-1">
                <li><a href="https://www.xverse.app/download" target="_blank" rel="noopener noreferrer" className="text-orange-400 underline">Xverse Wallet</a></li>
                <li><a href="https://leather.io/install-extension" target="_blank" rel="noopener noreferrer" className="text-purple-400 underline">Leather Wallet</a></li>
              </ul>
            </div>
          ),
          duration: 15000
        });
        setIsLoading(false);
        return;
      }
      
      // Use wallet_connect which works on both Xverse and Leather
      // Try with 'xverse' providerId first, fall back to auto-detect
      console.log('[WALLET] Calling request(wallet_connect)...');
      
      let response;
      try {
        // First try with explicit xverse provider
        response = await request('wallet_connect', {
          addresses: [AddressPurpose.Stacks, AddressPurpose.Payment, AddressPurpose.Ordinals],
          message: 'STXOSHI Treasury Agent'
        }, 'xverse');
      } catch (xverseErr) {
        console.log('[WALLET] Xverse providerId failed, trying auto-detect:', xverseErr.message);
        // Fall back to auto-detect (no providerId)
        response = await request('wallet_connect', {
          addresses: [AddressPurpose.Stacks, AddressPurpose.Payment, AddressPurpose.Ordinals],
          message: 'STXOSHI Treasury Agent'
        });
      }
      
      console.log('[WALLET] wallet_connect response:', JSON.stringify(response, null, 2));
      
      if (response.status === 'success') {
        const addrs = response.result?.addresses || response.result;
        
        // Find different address types
        const stxAddr = addrs?.find(a => 
          a.purpose === AddressPurpose.Stacks || 
          a.purpose === 'stacks' ||
          a.address?.startsWith('SP') ||
          a.address?.startsWith('SM')
        );
        const btcAddr = addrs?.find(a => a.purpose === AddressPurpose.Payment || a.purpose === 'payment');
        const ordAddr = addrs?.find(a => a.purpose === AddressPurpose.Ordinals || a.purpose === 'ordinals');
        
        const walletData = {
          stx: stxAddr?.address,
          btc: btcAddr?.address,
          ordinals: ordAddr?.address,
          publicKey: stxAddr?.publicKey || null
        };
        
        console.log('[WALLET] Connected addresses:', walletData);
        
        if (walletData.stx) {
          safeSetItem(WALLET_STORAGE_KEY, JSON.stringify(walletData));
          setAddresses(walletData);
          setConnected(true);
          
          const userData = await fetchUser(walletData.stx);
          toast.success(`Connected: ${userData?.username || walletData.stx.slice(0, 8)}...`);
          return true;
        } else {
          throw new Error('No Stacks address received from wallet');
        }
      } else if (response.error) {
        console.error('[WALLET] Connect error:', response.error);
        if (response.error.code === RpcErrorCode?.USER_REJECTION) {
          toast.info('Connection cancelled');
        } else {
          throw new Error(response.error.message || 'Connection failed');
        }
      }
    } catch (e) {
      console.error('[WALLET] connectWallet error:', e);
      toast.error('Failed to connect wallet', { 
        description: e.message || 'Please make sure your wallet extension is installed and unlocked'
      });
    } finally {
      setIsLoading(false);
    }
    return false;
  }, []);

  const disconnectWallet = useCallback(async () => {
    console.log('[WALLET] === DEEP DISCONNECT ===');
    
    // Step 1: Call BOTH wallet provider disconnect methods to revoke the session
    // This is CRITICAL to force account re-selection on next connect
    
    // Method 1: wallet_disconnect - clears app permissions for all accounts
    try {
      console.log('[WALLET] Calling wallet_disconnect RPC...');
      const disconnectResponse = await request('wallet_disconnect', undefined);
      console.log('[WALLET] wallet_disconnect response:', disconnectResponse);
    } catch (e) {
      console.log('[WALLET] wallet_disconnect failed:', e.message);
    }
    
    // Method 2: wallet_renouncePermissions - explicitly renounce all permissions
    try {
      console.log('[WALLET] Calling wallet_renouncePermissions RPC...');
      const renounceResponse = await request('wallet_renouncePermissions', undefined);
      console.log('[WALLET] wallet_renouncePermissions response:', renounceResponse);
    } catch (e) {
      console.log('[WALLET] wallet_renouncePermissions failed:', e.message);
    }
    
    // Step 2: Clear all local storage related to wallets
    clearWalletSession();
    
    // Step 3: Deep clean - clear any IndexedDB data that wallets might use
    try {
      if (window.indexedDB) {
        const databases = ['xverse', 'leather', 'stacks', 'wallet', 'sats-connect'];
        databases.forEach(dbName => {
          try {
            window.indexedDB.deleteDatabase(dbName);
            console.log('[WALLET] Deleted IndexedDB:', dbName);
          } catch (e) {
            // Ignore - database might not exist
          }
        });
      }
    } catch (e) {
      console.log('[WALLET] IndexedDB cleanup error (non-critical):', e);
    }
    
    // Step 4: Nuclear option - clear ALL localStorage keys related to wallets
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes('xverse') || 
          key.includes('Xverse') ||
          key.includes('leather') || 
          key.includes('Leather') ||
          key.includes('stacks') || 
          key.includes('Stacks') ||
          key.includes('wallet') ||
          key.includes('Wallet') ||
          key.includes('sats') ||
          key.includes('connect') ||
          key.includes('Connect') ||
          key.includes('auth') ||
          key.includes('session') ||
          key.includes('account') ||
          key.includes('Address') ||
          key.includes('address') ||
          key.includes('permission') ||
          key.includes('Permission')
        )) {
          keysToRemove.push(key);
        }
      }
      console.log('[WALLET] Removing localStorage keys:', keysToRemove);
      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {}
      });
      
      // Also clear sessionStorage completely
      try {
        sessionStorage.clear();
        console.log('[WALLET] Cleared sessionStorage');
      } catch (e) {}
      
    } catch (e) {
      console.log('[WALLET] Storage cleanup error (non-critical):', e);
    }
    
    // Step 5: Reset React state
    setConnected(false);
    setUser(null);
    setAddresses({ stx: null, btc: null, ordinals: null, publicKey: null });
    
    toast.success('Wallet fully disconnected', {
      description: 'You will be asked to select an account on next connect'
    });
    
    console.log('[WALLET] === DISCONNECT COMPLETE ===');
  }, []);

  // === STX TRANSFER ===
  const signStxTransfer = useCallback(async ({ recipient, amount, memo }) => {
    console.log('[WALLET] === STX TRANSFER ===');
    console.log('[WALLET] Params:', { recipient, amount, memo });
    console.log('[WALLET] isMobile:', isMobile());
    console.log('[WALLET] hasWalletProvider:', hasWalletProvider());
    
    if (!connected || !addresses.stx) {
      throw new Error('Wallet not connected');
    }
    
    if (txInProgress.current) {
      throw new Error('Transaction in progress');
    }
    
    txInProgress.current = true;
    
    try {
      // Ensure wallet is on Mainnet before transaction (desktop only)
      if (!isMobile()) {
        try {
          console.log('[WALLET] Ensuring wallet is on Mainnet...');
          await request('wallet_changeNetwork', { name: 'Mainnet' });
          console.log('[WALLET] Network set to Mainnet');
        } catch (networkErr) {
          console.log('[WALLET] Could not change network (may already be mainnet):', networkErr.message);
        }
      }
      
      // Verify the wallet's active account matches our connected address
      try {
        const accountCheck = await request('wallet_getAccount', { 
          addresses: ['stacks'] 
        }, 'xverse');  // Force Xverse provider
        const activeStxAddr = accountCheck?.result?.addresses?.find(
          a => a.purpose === 'stacks' || a.address?.startsWith('SP')
        )?.address;
        
        if (activeStxAddr && activeStxAddr !== addresses.stx) {
          throw new Error(`Wallet mismatch: App connected to ${addresses.stx?.slice(-6)} but wallet is using ${activeStxAddr?.slice(-6)}. Please switch accounts in Xverse or reconnect.`);
        }
      } catch (checkError) {
        if (checkError.message?.includes('Wallet mismatch')) throw checkError;
        console.warn('[WALLET] Could not verify active account:', checkError.message);
      }
      
      // On desktop with Xverse extension, try using the provider directly first
      // This ensures the popup actually opens
      const xverseProvider = getXverseProvider();
      console.log('[WALLET] Provider check:', {
        isMobile: isMobile(),
        hasProvider: !!xverseProvider,
        hasRequest: typeof xverseProvider?.request === 'function',
        providerType: typeof xverseProvider
      });
      
      if (!isMobile() && xverseProvider && typeof xverseProvider.request === 'function') {
        console.log('[WALLET] Desktop detected - using Xverse provider directly...');
        console.log('[WALLET] Calling provider.request("stx_transferStx")...');
        try {
          const providerResponse = await xverseProvider.request('stx_transferStx', {
            recipient: recipient,
            amount: Number(amount),
            memo: memo || ''
          });
          
          console.log('[WALLET] Provider response:', JSON.stringify(providerResponse, null, 2));
          
          if (providerResponse.result) {
            return {
              success: true,
              txid: providerResponse.result.txid || providerResponse.result,
              transaction: providerResponse.result.transaction || null
            };
          } else if (providerResponse.error) {
            throw new Error(providerResponse.error.message || 'Transaction failed');
          }
        } catch (providerError) {
          console.log('[WALLET] Provider direct call failed:', providerError);
          console.log('[WALLET] Falling back to sats-connect...');
          // Fall through to sats-connect
        }
      } else {
        console.log('[WALLET] Skipping desktop provider path, reason:', 
          isMobile() ? 'mobile device' : !xverseProvider ? 'no provider' : 'no request method');
      }
      
      // Always use stx_transferStx - simpler and works on all platforms
      // Try with 'xverse' providerId first, fall back to auto-detect
      console.log('[WALLET] Using stx_transferStx via sats-connect...');
      
      // Note: amount should be a Number per official docs
      let response;
      try {
        response = await request('stx_transferStx', {
          recipient: recipient,
          amount: Number(amount),
          memo: memo || ''
        }, 'xverse');
      } catch (xverseErr) {
        console.log('[WALLET] Xverse providerId failed, trying auto-detect:', xverseErr.message);
        response = await request('stx_transferStx', {
          recipient: recipient,
          amount: Number(amount),
          memo: memo || ''
        });
      }
      
      console.log('[WALLET] stx_transferStx response:', JSON.stringify(response, null, 2));
      
      if (response.status === 'success' || response.result) {
        const result = response.result || response;
        return {
          success: true,
          txid: result.txid || result,
          transaction: result.transaction || null
        };
      } else if (response.error) {
        if (response.error.code === RpcErrorCode?.USER_REJECTION) {
          throw new Error('Transaction rejected by user');
        }
        throw new Error(response.error.message || 'Transaction failed');
      }
      
      throw new Error('Unknown wallet response');
      
    } catch (e) {
      console.error('[WALLET] STX transfer error:', e);
      throw e;
    } finally {
      txInProgress.current = false;
    }
  }, [connected, addresses.stx]);

  // === AGENT DEPLOYMENT ===
  // Uses STXOSHI token transfer instead of STX to avoid network mismatch issues on desktop
  const signAgentDeployment = useCallback(async (agentName, agentId) => {
    console.log('[WALLET] === AGENT DEPLOYMENT (STXOSHI) ===');
    
    if (!connected || !addresses.stx) {
      throw new Error('Wallet not connected');
    }
    
    // STXOSHI Treasury Address (VWN Hot Wallet)
    const STXOSHI_TREASURY = 'SP3VVPEFZNN84EGMD0GN4XWAZ4X5ZMWC852NGWVWN';
    const STXOSHI_CONTRACT = 'SP1Z92MPDQEWZXW36VX71Q25HKF5K2EPCJ304F275.tokensoft-token-v4karmaedcr';
    const deployFee = 1000000; // 1 STXOSHI in micro (same as deposit method)
    
    const memo = `STXOSHI:DEPLOY:${agentId.slice(0, 16)}:${agentName.slice(0, 20)}`;
    
    console.log('[WALLET] Deployment params:', { deployFee, treasury: STXOSHI_TREASURY, memo });
    
    toast.info('Please approve the transaction in your wallet', {
      description: '1 STXOSHI registration fee',
      duration: 15000
    });
    
    try {
      // Use stx_callContract (same as deposits) to avoid stx_transferStx network mismatch
      const satsConnect = await import('sats-connect');
      const stacksTx = await import('@stacks/transactions');
      
      // Build post-condition
      const postCondition = stacksTx.Pc.principal(addresses.stx)
        .willSendEq(BigInt(deployFee))
        .ft(STXOSHI_CONTRACT, 'tokensoft-token');
      
      const payload = {
        contract: STXOSHI_CONTRACT,
        functionName: 'transfer',
        arguments: [
          stacksTx.cvToHex(stacksTx.uintCV(deployFee)),
          stacksTx.cvToHex(stacksTx.principalCV(addresses.stx)),
          stacksTx.cvToHex(stacksTx.principalCV(STXOSHI_TREASURY)),
          stacksTx.cvToHex(stacksTx.noneCV())
        ],
        postConditions: [stacksTx.postConditionToHex(postCondition)],
        postConditionMode: 'deny'
      };
      
      console.log('[WALLET] Using stx_callContract for deployment...');
      const response = await satsConnect.request('stx_callContract', payload);
      
      console.log('[WALLET] stx_callContract response:', JSON.stringify(response, null, 2));
      
      if (!response?.result?.txid) {
        if (response?.error?.message) {
          throw new Error(response.error.message);
        }
        throw new Error('Transaction cancelled or failed');
      }
      
      const txid = response.result.txid;
      
      toast.success('Transaction signed!', {
        description: `TX: ${txid.slice(0, 16)}...`
      });
      
      return {
        success: true,
        txid: txid,
        memo
      };
    } catch (e) {
      console.error('[WALLET] Deployment error:', e);
      
      if (e.message.includes('denied') || e.message.includes('reject') || e.message.includes('cancel')) {
        toast.error('Transaction cancelled by user');
      } else {
        toast.error('Transaction failed', { description: e.message });
      }
      throw e;
    }
  }, [connected, addresses.stx, signStxTransfer]);

  // === CONTRACT CALL ===
  // Uses sats-connect for mobile wallet compatibility
  const signContractCall = useCallback(async ({ contractAddress, contractName, functionName, functionArgs = [], postConditions = [], postConditionMode = 'deny', stxAmount = 0 }) => {
    console.log('[WALLET] === CONTRACT CALL ===');
    console.log('[WALLET] Contract:', contractAddress, contractName);
    console.log('[WALLET] Function:', functionName);
    console.log('[WALLET] Args count:', functionArgs.length);
    console.log('[WALLET] Post-conditions received:', postConditions);
    console.log('[WALLET] Post-conditions count:', postConditions?.length || 0);
    console.log('[WALLET] Post-condition mode:', postConditionMode);
    console.log('[WALLET] STX Amount:', stxAmount);
    
    if (!connected || !addresses.stx) {
      throw new Error('Wallet not connected');
    }
    
    try {
      // Import serialization helpers dynamically
      const { cvToHex, postConditionToHex } = await import('@stacks/transactions');
      
      // Serialize function args to hex strings for sats-connect
      const serializedArgs = functionArgs.map(arg => {
        // If arg is already a Clarity value, convert to hex
        if (arg && typeof arg === 'object' && arg.type !== undefined) {
          return cvToHex(arg);
        }
        // If already a hex string, use as-is
        return arg;
      });
      
      console.log('[WALLET] Serialized args:', serializedArgs);
      
      // Serialize post-conditions to hex strings using postConditionToHex
      let serializedPostConditions = [];
      if (postConditions && postConditions.length > 0) {
        serializedPostConditions = postConditions.map(pc => {
          try {
            console.log('[WALLET] Serializing post-condition:', pc);
            const hex = postConditionToHex(pc);
            console.log('[WALLET] Post-condition hex:', hex);
            return hex;
          } catch (e) {
            console.error('[WALLET] Error serializing post-condition:', e);
            return null;
          }
        }).filter(Boolean);
      }
      
      console.log('[WALLET] Serialized post-conditions:', serializedPostConditions);
      
      const payload = {
        contract: `${contractAddress}.${contractName}`,
        functionName,
        arguments: serializedArgs,
        postConditionMode: postConditionMode  // 'deny' (strict) or 'allow' (permissive for complex protocols)
      };
      
      // Add post-conditions if we have any
      if (serializedPostConditions.length > 0) {
        payload.postConditions = serializedPostConditions;
      }
      
      console.log('[WALLET] Calling stx_callContract with payload:', JSON.stringify(payload, null, 2));
      
      // Try with 'xverse' providerId first, fall back to auto-detect
      let response;
      try {
        response = await request('stx_callContract', payload, 'xverse');
      } catch (xverseErr) {
        console.log('[WALLET] Xverse providerId failed, trying auto-detect:', xverseErr.message);
        response = await request('stx_callContract', payload);
      }
      
      console.log('[WALLET] Full response object:', JSON.stringify(response, null, 2));
      console.log('[WALLET] Response type:', typeof response);
      console.log('[WALLET] Response keys:', response ? Object.keys(response) : 'null');
      
      // Extract txid from various possible response structures
      let txid = null;
      
      // Try different paths where txid might be
      if (response?.result?.txid) {
        txid = response.result.txid;
        console.log('[WALLET] Found txid in response.result.txid:', txid);
      } else if (response?.txid) {
        txid = response.txid;
        console.log('[WALLET] Found txid in response.txid:', txid);
      } else if (typeof response === 'string' && response.length === 66) {
        // Some wallets return txid directly as string
        txid = response;
        console.log('[WALLET] Response is txid string:', txid);
      } else if (response?.result && typeof response.result === 'string' && response.result.length === 66) {
        txid = response.result;
        console.log('[WALLET] Found txid in response.result as string:', txid);
      }
      
      if (txid) {
        console.log('[WALLET] Contract call successful, txid:', txid);
        return { 
          success: true, 
          txid: txid
        };
      }
      
      // Check for success status without txid (might be pending)
      if (response && (response.status === 'success' || response.result)) {
        console.log('[WALLET] Response indicates success but no txid found');
        console.log('[WALLET] response.status:', response.status);
        console.log('[WALLET] response.result:', response.result);
        
        // If result has transaction hex, we can try to extract txid
        if (response.result?.transaction || response.transaction) {
          console.log('[WALLET] Found transaction hex, but no txid');
        }
        
        throw new Error('Transaction was signed but txid not returned. Please check your wallet.');
      }
      
      // Handle error response
      if (response && response.error) {
        throw new Error(response.error.message || 'Contract call failed');
      }
      
      throw new Error('Contract call did not return expected result');
    } catch (e) {
      console.error('[WALLET] Contract call error:', e);
      
      // Re-throw with cleaner message
      if (e.message?.includes('rejected') || e.message?.includes('cancel') || e.code === RpcErrorCode.USER_REJECTION) {
        throw new Error('Transaction was rejected or cancelled');
      }
      
      throw e;
    }
  }, [connected, addresses.stx]);

  const updateUser = useCallback(async (updates) => {
    if (!addresses.stx) return null;
    try {
      const res = await axios.patch(`${API}/auth/me`, updates, {
        headers: { 'X-STX-Address': addresses.stx }
      });
      setUser(res.data);
      return res.data;
    } catch (e) {
      return null;
    }
  }, [addresses.stx]);

  const refreshUser = useCallback(async () => {
    if (!addresses.stx) return null;
    try {
      const res = await axios.get(`${API}/users/address/${addresses.stx}`);
      setUser(res.data);
      return res.data;
    } catch (e) {
      console.log('[WALLET] Failed to refresh user:', e);
      return null;
    }
  }, [addresses.stx]);

  const getAuthHeaders = useCallback(() => {
    return addresses.stx ? { 'X-STX-Address': addresses.stx } : {};
  }, [addresses.stx]);

  const value = {
    isConnected: connected,
    isLoading,
    user,
    addresses,
    connectWallet,
    disconnectWallet,
    updateUser,
    refreshUser,
    getAuthHeaders,
    isMobile: isMobile(),
    showWalletModal,
    closeWalletModal: () => setShowWalletModal(false),
    openXverseMobile,
    openLeatherMobile,
    clearProviderCache: clearWalletSession,
    signStxTransfer,
    signContractCall,
    signAgentDeployment,
    forceCleanWalletSession: clearWalletSession,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
      
      {/* Mobile Wallet Selection Modal */}
      {showWalletModal && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowWalletModal(false)}
        >
          <div 
            className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 max-w-sm w-full"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-2 text-center">Connect Wallet</h3>
            <p className="text-zinc-400 text-sm text-center mb-6">
              Open this site in the Xverse in-app browser
            </p>
            
            <button
              onClick={() => { 
                setShowWalletModal(false); 
                openXverseMobile(); 
              }}
              className="w-full flex items-center gap-4 p-4 bg-zinc-800 hover:bg-zinc-700 rounded-xl mb-3 border border-orange-500/50"
            >
              <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#EE7A30"/>
                  <path d="M2 17l10 5 10-5M2 12l10 5 10-5" stroke="#EE7A30" strokeWidth="2"/>
                </svg>
              </div>
              <div className="text-left">
                <div className="font-semibold text-white">
                  Open in Xverse
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded ml-2">Required</span>
                </div>
                <div className="text-xs text-zinc-400">Opens site in wallet browser</div>
              </div>
            </button>
            
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4 text-xs text-blue-400">
              <p className="font-medium mb-1">💡 Why Xverse browser?</p>
              <p className="text-blue-300/80">
                iOS browsers can't connect directly to wallets. 
                Open this site in Xverse's built-in browser for full functionality.
              </p>
            </div>
            
            <div className="bg-zinc-800/50 rounded-lg p-3 mb-4 text-xs text-zinc-500">
              <p className="font-medium text-zinc-400 mb-1">Manual steps:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Open Xverse app</li>
                <li>Tap "Explore" at bottom</li>
                <li>Enter site URL</li>
              </ol>
            </div>
            
            <button 
              onClick={() => setShowWalletModal(false)} 
              className="w-full py-3 text-zinc-400 hover:text-white text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) throw new Error('useWallet must be used within WalletProvider');
  return context;
}

export default WalletContext;
